import {
  Link as RouterLink,
  useInRouterContext,
  useLocation,
  useNavigate,
} from "react-router-dom"
import type { ComponentProps, ReactNode } from "react"

type Href = string | { pathname?: string; search?: string; hash?: string }

function hrefToString(href: Href): string {
  if (typeof href === "string") return href
  return `${href.pathname || ""}${href.search || ""}${href.hash || ""}`
}

/**
 * Small routing boundary used by feature code. Keeping navigation behind this
 * module lets the screens remain independent from the router implementation.
 */
export function useRouter() {
  const inRouter = useInRouterContext()
  const navigate = inRouter ? useNavigate() : null

  return {
    push: (href: Href) => {
      const target = hrefToString(href)
      if (navigate) navigate(target)
      else window.history.pushState({}, "", target)
    },
    replace: (href: Href) => {
      const target = hrefToString(href)
      if (navigate) navigate(target, { replace: true })
      else window.history.replaceState({}, "", target)
    },
    prefetch: async (_href: Href) => undefined,
    back: () => {
      if (navigate) navigate(-1)
      else window.history.back()
    },
  }
}

export function usePathname() {
  const inRouter = useInRouterContext()
  const location = inRouter ? useLocation() : null
  return location?.pathname || (typeof window === "undefined" ? "/" : window.location.pathname)
}

type LinkProps = Omit<ComponentProps<typeof RouterLink>, "to"> & { href: Href; children?: ReactNode }

export function Link({ href, ...props }: LinkProps) {
  return <RouterLink to={hrefToString(href)} {...props} />
}

export default Link
